import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl-next';

import Link from '@atlaskit/link';

import { usePasteDetection } from '@confluence/editor-features/entry-points/usePasteDetection';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

interface UseLivePagesAITitleGenerationProps {
	contentId: string;
	userOffline?: boolean;
	title: string;
	isUserEditing: boolean;
	onTitleUpdate: (value: string) => Promise<void>;
	flags?: FlagsStateContainer;
	formatMessage: IntlShape['formatMessage'];
	i18n: any;
	isEditorEmpty?: boolean;
}

interface UseLivePagesAITitleGenerationReturn {
	isGeneratingTitle: boolean;
	isTitleAIGenerated: boolean;
	documentContext: string;
	onAITitleChange: (newTitle: string) => void;
	resetAIGeneratedFlag: () => void;
}

export const useLivePagesAITitleGeneration = ({
	contentId,
	userOffline,
	title,
	isUserEditing,
	onTitleUpdate,
	flags,
	formatMessage,
	i18n,
	isEditorEmpty,
}: UseLivePagesAITitleGenerationProps): UseLivePagesAITitleGenerationReturn => {
	const { documentContext, pasteDetected } = usePasteDetection({
		contentId,
		userOffline,
		isEditorEmpty: isEditorEmpty ?? false,
	});

	const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
	const [isTitleAIGenerated, setIsTitleAIGenerated] = useState(false);
	const prevIsGeneratingTitleRef = useRef(isGeneratingTitle);
	const aiGeneratedTitleRef = useRef<string | null>(null);
	const hasCompletedGenerationRef = useRef(false);

	const onAITitleChange = useCallback(
		(newTitle: string) => {
			if (!hasCompletedGenerationRef.current) {
				hasCompletedGenerationRef.current = true;
				aiGeneratedTitleRef.current = newTitle;
				void onTitleUpdate(newTitle);
			}
			setIsGeneratingTitle(false);
		},
		[onTitleUpdate],
	);

	const actualTitle = useMemo(() => {
		if (!pasteDetected && !isGeneratingTitle) {
			return null;
		}
		const titleInput = document.querySelector(
			'[data-ai-suggest-title-target="true"]',
		) as HTMLTextAreaElement | null;
		return titleInput?.value || '';
	}, [pasteDetected, isGeneratingTitle]);

	useEffect(() => {
		if (isGeneratingTitle) {
			hasCompletedGenerationRef.current = false;
		}
	}, [isGeneratingTitle]);

	useEffect(() => {
		if (
			pasteDetected &&
			!isGeneratingTitle &&
			!isUserEditing &&
			(!actualTitle || actualTitle.trim() === '')
		) {
			setIsGeneratingTitle(true);
		}
	}, [pasteDetected, actualTitle, isGeneratingTitle, isUserEditing]);

	useEffect(() => {
		if (aiGeneratedTitleRef.current && title === aiGeneratedTitleRef.current) {
			setIsTitleAIGenerated(true);
			aiGeneratedTitleRef.current = null;
		}
	}, [title]);

	useEffect(() => {
		if (flags && prevIsGeneratingTitleRef.current && !isGeneratingTitle) {
			void flags.hideFlag('ai-generated-title-from-paste-flag');
			void flags.showInfoFlag({
				id: 'ai-generated-title-from-paste-flag',
				title: formatMessage(i18n.aiGeneratedTitleFromPaste),
				description: formatMessage(i18n.aiGeneratedTitleFromPasteDescription, {
					Link: (chunks: React.ReactNode[]) => (
						<Link target="_blank" href="https://www.atlassian.com/trust/atlassian-intelligence">
							{chunks}
						</Link>
					),
				}),
				actions: [
					{
						content: formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-from-paste-flag'),
					},
				],
			});
		}

		prevIsGeneratingTitleRef.current = isGeneratingTitle;
	}, [isGeneratingTitle, formatMessage, flags, i18n]);

	const resetAIGeneratedFlag = useCallback(() => {
		setIsTitleAIGenerated(false);
	}, []);

	return {
		isGeneratingTitle,
		isTitleAIGenerated,
		documentContext,
		onAITitleChange,
		resetAIGeneratedFlag,
	};
};
